import React, { useState, useEffect, useRef } from 'react';
import AskAlex from './AskAlex';
import { api } from '../../shared/utils/api';

/* ── Free calibration quiz ───────────────────────────────────────────────── */
const LETTERS = ['A', 'B', 'C', 'D', 'E'];

// Default threshold values for badge styling — display only
const DEFAULT_TH_RED = 0.4;
const DEFAULT_TH_GREEN = 0.7;

function QuizPanel({ topicId, oldConfidence, onClose, onAllAnswered }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [newConfidence, setNewConfidence] = useState(undefined); // undefined = not yet fetched
  const prefetchRef = useRef(null);

  useEffect(() => {
    api(`/api/questions/topic/${topicId}/free`).then(data => {
      setQuestions(data.questions || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [topicId]);

  const total = questions.length;
  const currentQ = questions[currentIdx];
  const isLast = currentIdx === total - 1;

  const handleSelect = (letter) => { if (!result) setSelected(letter); };

  const handleSubmit = async () => {
    if (!selected || submitting || !currentQ) return;
    setSubmitting(true);
    const res = await api('/api/questions/answer', {
      method: 'POST',
      body: JSON.stringify({ question_id: currentQ.id, selected_answer: selected }),
    });
    const newAnswers = [...answers, { correct: res.correct }];
    setAnswers(newAnswers);
    setResult(res);
    setSubmitting(false);
    // Pre-fetch new confidence when last question is submitted
    if (newAnswers.length === total) {
      prefetchRef.current = onAllAnswered();
    }
  };

  const handleNext = () => {
    setCurrentIdx(i => i + 1);
    setSelected(null);
    setResult(null);
  };

  const handleSeeResults = async () => {
    setShowResults(true);
    if (prefetchRef.current) {
      const conf = await prefetchRef.current;
      setNewConfidence(conf);
    }
  };

  const getBadgeStyle = (conf) => {
    if (conf === null || conf === undefined) return { bg: '#E5E7EB', color: '#6b6b68' };
    if (conf < DEFAULT_TH_RED) return { bg: '#FEE2E2', color: '#991B1B' };
    if (conf < DEFAULT_TH_GREEN) return { bg: '#FEF3C7', color: '#92400E' };
    return { bg: '#DCFCE7', color: '#166534' };
  };

  if (loading) {
    return (
      <div className="quiz-panel" onClick={e => e.stopPropagation()}>
        <div className="quiz-loading">Loading questions</div>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="quiz-panel" onClick={e => e.stopPropagation()}>
        <div className="quiz-loading">No questions available for this topic.</div>
        <button className="quiz-close-btn" onClick={onClose}>Close</button>
      </div>
    );
  }

  if (showResults) {
    const correct = answers.filter(a => a.correct).length;
    const pct = correct / total;
    const scoreColor = pct >= 0.6 ? '#166534' : pct >= 0.4 ? '#92400E' : '#991B1B';
    const oldPct = oldConfidence !== null && oldConfidence !== undefined ? Math.round(oldConfidence * 100) : null;
    const newPct = newConfidence !== undefined && newConfidence !== null ? Math.round(newConfidence * 100) : null;
    const oldStyle = getBadgeStyle(oldConfidence);
    const newStyle = getBadgeStyle(newConfidence);

    return (
      <div className="quiz-panel quiz-results-panel" onClick={e => e.stopPropagation()}>
        <div className="quiz-results-score" style={{ color: scoreColor }}>
          You got {correct} of {total} right
        </div>
        <div className="quiz-results-conf">
          {newConfidence === undefined
            ? 'Updating your readiness score'
            : newPct !== null && oldPct !== null
              ? `Your readiness score updated from ${oldPct}% to ${newPct}%.`
              : newPct !== null
                ? `Your readiness score is now ${newPct}%.`
                : null}
        </div>
        <button className="quiz-close-btn" onClick={onClose}>Close</button>
      </div>
    );
  }

  // Build options list from dict or array
  const optionsList = currentQ.options
    ? Array.isArray(currentQ.options)
      ? currentQ.options
      : LETTERS.filter(l => currentQ.options[l]).map(l => ({ letter: l, text: currentQ.options[l] }))
    : [];

  return (
    <div className="quiz-panel" onClick={e => e.stopPropagation()}>
      {/* Progress */}
      <div className="quiz-progress-row">
        <span className="quiz-progress-label">Question {currentIdx + 1} of {total}</span>
        <div className="quiz-dots">
          {Array.from({ length: total }, (_, i) => {
            let cls = 'quiz-dot';
            if (i < answers.length) cls += answers[i].correct ? ' answered-right' : ' answered-wrong';
            else if (i === currentIdx) cls += ' current';
            return <span key={i} className={cls} />;
          })}
        </div>
      </div>

      {/* Scenario */}
      {currentQ.scenario && (
        <div className="quiz-scenario">{currentQ.scenario}</div>
      )}

      {/* Stem */}
      <div className="quiz-stem">{currentQ.stem}</div>

      {/* Options */}
      <div className="quiz-options">
        {optionsList.map(opt => {
          const letter = opt.letter;
          const text = opt.text;
          const isCorrect = result && letter === result.correct_answer;
          const isWrong = result && letter === selected && !result.correct;
          const isGreyed = result && !isCorrect && !isWrong;
          const isSelected = !result && letter === selected;

          const optClass = [
            'quiz-option',
            isSelected ? 'quiz-opt-selected' : '',
            isCorrect ? 'quiz-opt-correct' : '',
            isWrong ? 'quiz-opt-wrong' : '',
            isGreyed ? 'quiz-opt-greyed' : '',
          ].filter(Boolean).join(' ');

          const letterClass = [
            'quiz-letter',
            isCorrect ? 'quiz-letter-correct' : '',
            isWrong ? 'quiz-letter-wrong' : '',
          ].filter(Boolean).join(' ');

          return (
            <button key={letter} className={optClass} onClick={() => handleSelect(letter)}>
              <span className={letterClass}>{letter}</span>
              <span className="quiz-opt-text">{text}</span>
            </button>
          );
        })}
      </div>

      {/* Explanation */}
      {result && (
        <div className="quiz-explanation">
          <div className="quiz-explanation-label">Explanation</div>
          <div className="quiz-explanation-text">{result.explanation}</div>
          {!result.correct && result.distractor_misconceptions && result.selected_answer &&
            result.distractor_misconceptions[result.selected_answer] && (
            <div className="quiz-distractor">
              <span className="quiz-distractor-label">Why {result.selected_answer} is wrong:</span>{' '}
              {result.distractor_misconceptions[result.selected_answer]}
            </div>
          )}
          {result.authority && <div className="quiz-authority">{result.authority}</div>}
        </div>
      )}

      {/* Ask Alex — appears after any answered question */}
      {result && currentQ && (
        <AskAlex questionId={currentQ.id} wasCorrect={result.correct} />
      )}

      {/* Action button */}
      {!result ? (
        <button className="quiz-check-btn" disabled={!selected || submitting} onClick={handleSubmit}>
          {submitting ? 'Checking' : 'Check answer'}
        </button>
      ) : isLast ? (
        <button className="quiz-next-btn" onClick={handleSeeResults}>See results</button>
      ) : (
        <button className="quiz-next-btn" onClick={handleNext}>Next question →</button>
      )}
    </div>
  );
}

export default QuizPanel;
